import React, {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CellValue, Column } from "react-table";
import { Checkbox } from "@src/components/elements/Checkbox";
import { Table } from "@src/components/modules/Table";
import { formatMoney } from "@src/utils/money";
import { TogglePanel } from "@src/components/modules";
import styled from "styled-components/macro";
import {
  Button,
  SearchIcon,
  Spinner,
  TextInput,
} from "@src/components/elements";
import _ from "lodash";
import { GetErpVendorsWithoutMappingsQuery } from "@src/__generated__/graphql";

const StyledRoot = styled.div`
  height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .action-buttons {
    background-color: var(--page-background);
    position: sticky;
    bottom: calc(-1rem);
    padding: 1rem;
    border-top: 1px solid var(--border-gray);
  }

  .search-input {
    max-width: 20rem;
    .search-icon {
      width: 1.6rem;
      height: auto;
      fill: var(--dark-gray);
    }
  }
`;

interface Props {
  mappedErpVendors: ErpVendor[];
  erpVendorsWithoutMappings?: ErpVendor[];
  onCancel: () => void;
  onSave: (
    removedErpVendors: ErpVendor[],
    addedErpVendors: ErpVendor[],
  ) => void;
  loading: boolean;
}

export type ErpVendor = Omit<
  GetErpVendorsWithoutMappingsQuery["erpVendorsWithoutErpVendorMappings"][0],
  "__typename"
>;

export const ErpVendorToVendorEditor: React.FC<Props> = ({
  mappedErpVendors,
  erpVendorsWithoutMappings,
  onCancel,
  onSave,
  loading,
}) => {
  const [includedErpVendors, setIncludedErpVendors] =
    useState<ErpVendor[]>(mappedErpVendors);
  const [excludedErpVendors, setExcludedErpVendors] = useState<
    ErpVendor[] | undefined
  >(erpVendorsWithoutMappings);

  useEffect(() => {
    if (!loading && erpVendorsWithoutMappings) {
      setExcludedErpVendors(erpVendorsWithoutMappings);
    }
  }, [erpVendorsWithoutMappings, loading]);

  const isIncluded = useCallback(
    (erpVendor: ErpVendor) => {
      return includedErpVendors.some(
        (includedErpVendor) => includedErpVendor.id === erpVendor.id,
      );
    },
    [includedErpVendors],
  );

  const addErpVendor = useCallback(
    (erpVendor: ErpVendor) => {
      setExcludedErpVendors(
        excludedErpVendors?.filter((v) => v.id !== erpVendor.id),
      );
      setIncludedErpVendors([...includedErpVendors, erpVendor]);
    },
    [excludedErpVendors, includedErpVendors],
  );

  const removeErpVendor = useCallback(
    (erpVendor: ErpVendor) => {
      setIncludedErpVendors(
        includedErpVendors.filter((v) => v.id !== erpVendor.id),
      );
      setExcludedErpVendors([...(excludedErpVendors ?? []), erpVendor]);
    },
    [excludedErpVendors, includedErpVendors],
  );

  const onSearchErpVendors = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const searchTerm = event.target.value;
      if (!searchTerm) {
        setExcludedErpVendors(erpVendorsWithoutMappings);
        return;
      }
      setExcludedErpVendors(
        excludedErpVendors?.filter((v) =>
          v.name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
        ),
      );
    },
    [erpVendorsWithoutMappings, excludedErpVendors],
  );

  const columns: Column<ErpVendor>[] = useMemo(
    () => [
      {
        Header: "Link",
        Cell: function Cell({ row }: CellValue) {
          return (
            <Checkbox
              label=""
              value={isIncluded(row.original)}
              onClick={() => {
                if (isIncluded(row.original)) {
                  removeErpVendor(row.original);
                } else {
                  addErpVendor(row.original);
                }
              }}
            />
          );
        },
      },
      {
        Header: "ERP Vendor",
        accessor: "name",
      },
      {
        Header: "L12M Spend",
        accessor: "last12Spend",
        Cell: function Cell({ value }) {
          return <span>{formatMoney(value, 0)}</span>;
        },
      },
    ],
    [addErpVendor, isIncluded, removeErpVendor],
  );

  return (
    <StyledRoot>
      {loading && (
        <div className="row center">
          <Spinner />
        </div>
      )}
      {excludedErpVendors && (
        <>
          <TogglePanel titleBar={<h3>Linked ERP vendors</h3>}>
            <Table
              data={includedErpVendors}
              columns={columns}
              pageSize={Number.MAX_SAFE_INTEGER}
            />
          </TogglePanel>

          <TogglePanel titleBar={<h3>Imported vendors</h3>}>
            <>
              <TextInput
                className="search-input"
                name="search"
                placeholder="Search by vendor"
                prefix={<SearchIcon className="search-icon" />}
                onChange={_.debounce(onSearchErpVendors, 10)}
              />
              <Table
                data={excludedErpVendors}
                columns={columns}
                pageSize={Number.MAX_SAFE_INTEGER}
              />
            </>
          </TogglePanel>
          <div className="action-buttons row">
            <Button variant="tertiary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const erpVendorsToAdd = _.difference(
                  includedErpVendors,
                  mappedErpVendors,
                );
                const erpVendorsToRemove = _.difference(
                  mappedErpVendors,
                  includedErpVendors,
                );
                onSave(erpVendorsToRemove, erpVendorsToAdd);
              }}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </StyledRoot>
  );
};
